import type { T2ICard } from '../../types/director'

const CAMERA_MOVEMENTS = [
  { value: 'push', label: '推' },
  { value: 'pull', label: '拉' },
  { value: 'pan', label: '摇' },
  { value: 'dolly', label: '移' },
  { value: 'follow', label: '跟' },
  { value: 'orbit', label: '环绕' },
  { value: 'static', label: '固定' },
  { value: 'crane', label: '升降' },
]

const RHYTHM_OPTIONS = [
  { value: 'tense', label: '紧张急促' },
  { value: 'gentle', label: '舒缓柔和' },
  { value: 'progressive', label: '渐进加速' },
  { value: 'explosive', label: '爆发冲击' },
  { value: 'dreamy', label: '梦幻飘逸' },
]

interface DirectorConsoleProps {
  selectedCard: T2ICard
  videoRequirement: string
  setVideoRequirement: (v: string) => void
  durationMode: '10s' | '30s'
  setDurationMode: (v: '10s' | '30s') => void
  isGeneratingI2V: boolean
  onGenerateI2V: () => void
  cameraMovements: string[]
  setCameraMovements: (v: string[]) => void
  rhythm: string
  setRhythm: (v: string) => void
}

export function DirectorConsole({
  selectedCard, videoRequirement, setVideoRequirement,
  durationMode, setDurationMode, isGeneratingI2V, onGenerateI2V,
  cameraMovements, setCameraMovements, rhythm, setRhythm,
}: DirectorConsoleProps) {
  const toggleCamera = (value: string) => {
    if (cameraMovements.includes(value)) {
      setCameraMovements(cameraMovements.filter((v) => v !== value))
    } else {
      setCameraMovements([...cameraMovements, value])
    }
  }

  return (
    <div className="bg-gray-800 rounded-lg p-3 mb-4 border border-gray-700">
      <h2 className="text-base font-semibold mb-2">🎬 第三步：设定视频动作与镜头</h2>
      <div className="grid md:grid-cols-2 gap-3">
        <div>
          <h3 className="font-semibold mb-1.5 text-sm">已选画面</h3>
          <div className="bg-gray-700 p-2 rounded-lg">
            <div className="mb-1">
              <span className="inline-block px-1.5 py-0.5 text-[10px] bg-purple-600 rounded">
                {selectedCard.style_tag}
              </span>
            </div>
            <h4 className="font-semibold mb-1 text-xs">{selectedCard.theme_name}</h4>
            <p className="text-xs text-gray-300 mb-2">{selectedCard.prompt}</p>
            <div className="flex gap-1.5">
              <button
                onClick={() => navigator.clipboard.writeText(selectedCard.prompt)}
                className="px-2 py-1 bg-green-600 hover:bg-green-700 rounded text-xs transition-colors"
              >
                📋 复制中文
              </button>
              {selectedCard.prompt_en && (
                <button
                  onClick={() => navigator.clipboard.writeText(selectedCard.prompt_en)}
                  className="px-2 py-1 bg-blue-600 hover:bg-blue-700 rounded text-xs transition-colors"
                >
                  📋 复制英文
                </button>
              )}
            </div>
          </div>
        </div>
        <div>
          <h3 className="font-semibold mb-1.5 text-sm">动作与剧情</h3>
          <textarea
            value={videoRequirement}
            onChange={(e) => setVideoRequirement(e.target.value)}
            placeholder="描述画面接下来发生什么，例如：角色缓缓转身，镜头拉远露出整座城市"
            className="w-full px-2 py-1.5 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-white mb-2 text-sm"
            rows={3}
            disabled={isGeneratingI2V}
          />

          {/* Camera movement multi-select */}
          <div className="mb-2">
            <label className="block text-xs font-medium mb-1">镜头运动偏好（可多选）</label>
            <div className="flex flex-wrap gap-1">
              {CAMERA_MOVEMENTS.map((cam) => (
                <button
                  key={cam.value}
                  onClick={() => toggleCamera(cam.value)}
                  disabled={isGeneratingI2V}
                  className={`px-2 py-0.5 rounded text-xs transition-colors ${
                    cameraMovements.includes(cam.value)
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-600 hover:bg-gray-500 text-gray-300'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {cam.label}
                </button>
              ))}
            </div>
          </div>

          {/* Rhythm single-select */}
          <div className="mb-2">
            <label className="block text-xs font-medium mb-1">节奏基调</label>
            <div className="flex flex-wrap gap-1">
              {RHYTHM_OPTIONS.map((r) => (
                <button
                  key={r.value}
                  onClick={() => setRhythm(rhythm === r.value ? '' : r.value)}
                  disabled={isGeneratingI2V}
                  className={`px-2 py-0.5 rounded text-xs transition-colors ${
                    rhythm === r.value
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-600 hover:bg-gray-500 text-gray-300'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>

          <div className="mb-2">
            <label className="block text-xs font-medium mb-1">视频时长模式</label>
            <div className="flex gap-2">
              <button
                onClick={() => setDurationMode('10s')}
                disabled={isGeneratingI2V}
                className={`flex-1 px-2 py-1 rounded-lg font-medium transition-colors text-xs ${
                  durationMode === '10s'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                单段 10 秒
              </button>
              <button
                onClick={() => setDurationMode('30s')}
                disabled={isGeneratingI2V}
                className={`flex-1 px-2 py-1 rounded-lg font-medium transition-colors text-xs ${
                  durationMode === '30s'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                三段 30 秒（含分镜衔接）
              </button>
            </div>
          </div>
          <button
            onClick={onGenerateI2V}
            disabled={isGeneratingI2V || !videoRequirement.trim()}
            className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed rounded-lg font-medium transition-colors text-sm"
          >
            {isGeneratingI2V ? '⏳ 正在生成分镜剧本...' : `🎞️ 生成${durationMode === '10s' ? '10秒' : '30秒'}分镜剧本`}
          </button>
        </div>
      </div>
    </div>
  )
}
